import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useApiClient from './useApiClient';
import usePermissions from '../auth/usePermissions';
import useMayPurchaseLicense from '../provider-billing/useMayPurchaseLicense';
import { isLicensePaywallActive, requiresLicensePurchase } from '../../services/tenant/providerBilling';

/**
 * What a restaurant may buy and already holds, for the licence paywall.
 *
 * Fetched only for tenants that actually sell licences, so the classic
 * dashboard makes no extra request — and only for a login that may buy: a
 * restaurant user is refused the read, and has nothing to be sold anyway, so
 * for them the paywall is simply not active. A super admin reads the same payload on
 * behalf of the partner, which is how the Partners surface can show what the
 * partner sees.
 *
 * Plain component state, not a shared cache: every trigger screen mounts its
 * own instance and fetches once. `error` is exposed so a screen can tell
 * "not licensed" from "could not find out" — the two must not look the same.
 *
 * The returned object and every function on it are stable between renders
 * (the repo rule for shared hooks): useLicenseGate lists them as dependencies.
 */
export default function useCompanyLicenseEntitlements(companyId) {
  const { PartnerLicensesManager, ProviderBillingLicensesManager, PartnerBillingManager } = useApiClient();
  const { isSuperAdmin } = usePermissions();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const company = useSelector((state) => state.currentCompany?.company);
  const mayPurchase = useMayPurchaseLicense();

  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  // The read currently on the wire, if any, as `{ key, promise }` — see
  // refresh(). Keyed, because the company can change while one is open.
  const inFlight = useRef(null);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // The partner's test restaurant is never billed, so it never meets the
  // paywall (the backend's LicenseGate agrees). Known for the selected
  // restaurant; a picker on another one meets the backend's refusal instead.
  const isTestRestaurant = Boolean(
    companyId && company?.is_test_restaurant && String(company.id) === String(companyId),
  );
  const active = isLicensePaywallActive(serviceProvider) && mayPurchase && !isTestRestaurant;
  // The backend's own word, once read: `billing_enabled` is false for the
  // master, an unmigrated partner and the test restaurant — including one a
  // screen picked without selecting it (the reservations company picker).
  const paywallActive = active && entitlements?.billing_enabled !== false;

  // A gate that stopped applying, or a screen that moved on to another
  // restaurant, must not keep showing the last restaurant's standing (a
  // "subscribed" tag, a held product — or a `billing_enabled: false` that
  // would wave the next restaurant through the paywall). The read on the
  // wire belongs to the old state too: dropping its entry keeps its answer
  // out, and the mount effect below starts the new one.
  useEffect(() => {
    inFlight.current = null;
    setEntitlements(null);
    setError(null);
    setLoading(false);
  }, [active, companyId]);

  const refresh = useCallback(async () => {
    if (!active || !companyId) {
      return null;
    }

    // What this read is FOR. A screen can switch restaurant (the reservations
    // company picker does, without remounting) while one is still open, so the
    // key decides whether a caller may join it.
    const key = `${companyId}:${isSuperAdmin}:${serviceProvider?.id}`;

    // One request at a time PER KEY. The mount effect fires this, and a gate
    // that awaits "the answer" during that same window must get the answer
    // already coming rather than start a second read: two in flight write the
    // same state, and if the first fails after the second succeeds the screen
    // ends up holding entitlements AND an error at once. A caller asking about
    // a different restaurant is not that case — it starts its own read, or it
    // would be handed the previous restaurant's standing.
    if (inFlight.current?.key === key) {
      return inFlight.current.promise;
    }

    // Identity, not just the key: switching A → B → A leaves two reads open
    // for the SAME key, and a key comparison cannot tell them apart. The first
    // one to answer would otherwise pass the guard with a payload that is one
    // restaurant-switch out of date, and clear the entry belonging to the read
    // still on the wire.
    const entry = { key, promise: null };
    inFlight.current = entry;

    entry.promise = (async () => {
      setLoading(true);
      setError(null);

      const { data, success } = isSuperAdmin
        ? await ProviderBillingLicensesManager.entitlements(companyId, serviceProvider?.id)
        : await PartnerLicensesManager.entitlements(companyId);

      // A screen the partner has already left must not be written to — nor one
      // that has moved on to another restaurant since this was sent, whose
      // answer would otherwise land in the same state as if it were theirs.
      if (!mounted.current || inFlight.current !== entry) {
        return null;
      }

      setLoading(false);

      if (!success) {
        // The stale payload goes with the error: a screen holding both reads
        // as "loaded" and hides the retry the failure needs.
        setEntitlements(null);
        setError(data ?? true);

        return null;
      }

      // Managers answer `{ data }` for resources and the bare object for a DTO,
      // so both shapes are accepted rather than assuming one.
      const resolved = data?.data ?? data;
      setError(null);
      setEntitlements(resolved);

      return resolved;
    })();

    try {
      return await entry.promise;
    } finally {
      // Only if nothing newer has taken its place — including another read of
      // the same restaurant, which shares this one's key but not its identity.
      if (inFlight.current === entry) {
        inFlight.current = null;
      }
    }
  }, [active, companyId, isSuperAdmin, serviceProvider?.id, PartnerLicensesManager, ProviderBillingLicensesManager]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Buy one product. Returns the refreshed entitlements on success so the
   * caller can render the success state without another round trip. `gate` is
   * the paywall the purchase came from, passed through to the backend.
   */
  const activate = useCallback(
    async (productKey, { gate = null } = {}) => {
      const { data, success, status } = await PartnerLicensesManager.activate(companyId, productKey, { gate });

      if (!success) {
        return { ok: false, status, error: data };
      }

      const resolved = data?.data ?? data;

      if (mounted.current) {
        setEntitlements(resolved);
      }

      return { ok: true, entitlements: resolved };
    },
    [companyId, PartnerLicensesManager],
  );

  /**
   * A Stripe Checkout (setup mode) session for the partner's billing card —
   * the step the paywall puts in front of a purchase when none is on file.
   */
  const createCardCheckout = useCallback(
    (urls) => PartnerBillingManager.createCheckoutSession(urls),
    [PartnerBillingManager],
  );

  /** Whether the paywall must open before `productKey` may be used. */
  const requiresPurchase = useCallback(
    (productKey) => paywallActive && requiresLicensePurchase(serviceProvider, entitlements, productKey),
    [paywallActive, serviceProvider, entitlements],
  );

  return useMemo(
    () => ({
      entitlements,
      loading,
      /** The failed entitlements response, when the last read did not succeed. */
      error,
      /** Whether this restaurant is sold licences at all. */
      paywallActive,
      requiresPurchase,
      refresh,
      activate,
      createCardCheckout,
    }),
    [entitlements, loading, error, paywallActive, requiresPurchase, refresh, activate, createCardCheckout],
  );
}
